import { gql } from "@apollo/client";
import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";

import type {
  MenuSectionNewResp,
  MenuSectionNewVariables,
  MenuSectionVariables,
  MenuSection
} from "../../types/menuSectionTypes";

const CREATE_MENU_SECTION = gql`
  mutation CreateMenuSection($attributes: CreateMenuSectionInput!) {
    createMenuSection(attributes: $attributes) {
      menuSection {
        id
        name
        position
        menuId
      }
      errors
    }
  }
` 

type UseCreateMenuSectionOptions = {
  onCompleted?: (menuSection: MenuSection) => void;
  onError?: (error: unknown) => void;
  mutationOptions?: Parameters<typeof useMutation<MenuSectionNewResp, MenuSectionNewVariables>>[1];
};

const useCreateMenuSection = (options?: UseCreateMenuSectionOptions) => {
  const [mutate, { data, loading, error }] = useMutation<
    MenuSectionNewResp,
    MenuSectionNewVariables
  >(CREATE_MENU_SECTION, options?.mutationOptions);

  const createMenuSection = useCallback(
    async (input: MenuSectionVariables) => {
      try {
        const result = await mutate({
          variables: {
            attributes: {
              name: input.name,
              menuId: input.menuId
            },
          },
        });

        const createMenuSection = result?.data?.createMenuSection;

        if (createMenuSection?.errors?.length) {
          throw new Error(createMenuSection.errors.join(", "));
        }

        if (options?.onCompleted && createMenuSection?.menuSection) {
          options.onCompleted(createMenuSection.menuSection);
        }

        return createMenuSection?.menuSection;
      } catch (err) {
        if (options?.onError) {
          options.onError(err);
        }
        throw err;
      }
    },
    [mutate, options]
  );

  const menuSection = data?.createMenuSection?.menuSection;

  return {
    createMenuSection,
    createdMenuSection: menuSection,
    loading,
    error: error as Error | null | undefined,
  };
}

export default useCreateMenuSection
